import * as Sentry from '@sentry/react'
import type { QueryClient } from '@tanstack/react-query'

const RECENT_QUERY_ENTRY_LIMIT = 100

type QueryCacheProbe = {
  totalEntries: number
  recentEntries: string[]
}

type DomProbe = {
  totalNodes: number
  bodyChildren: number
}

type MemoryInventory = {
  queryCache: QueryCacheProbe
  dom: DomProbe
}

export const sendMemoryInventoryToSentry = (
  queryClient: QueryClient,
  context: {
    pathname: string
    heap: {
      usedJSHeapSize: number
      totalJSHeapSize: number
      jsHeapSizeLimit: number
    }
  }
) => {
  // Best-effort: telemetry must not break threshold logging or reload.
  try {
    const inventory: MemoryInventory = {
      queryCache: probeQueryCache(queryClient),
      dom: probeDom()
    }

    Sentry.captureMessage('memory_threshold_exceeded', {
      level: 'warning',
      contexts: {
        heap: context.heap,
        memoryInventory: inventory
      },
      tags: { pathname: context.pathname }
    })
  } catch {
    // Swallow — see comment above.
  }
}

const probeQueryCache = (queryClient: QueryClient): QueryCacheProbe => {
  // QueryCache stores queries in a Map; getAll() preserves insertion order, so
  // the tail of the array represents the most recently added entries.
  const entries = queryClient.getQueryCache().getAll()
  const recentEntries = entries
    .slice(-RECENT_QUERY_ENTRY_LIMIT)
    .reverse()
    .map(query => JSON.stringify(query.queryKey))

  return {
    totalEntries: entries.length,
    recentEntries
  }
}

const probeDom = (): DomProbe => ({
  totalNodes: document.getElementsByTagName('*').length,
  bodyChildren: document.body.children.length
})
